<template>
    <b-form-row>
        <b-col>
            <label class="font-weight-bold">Rx Diagnosis Codes</label>
            <b-form-row cols-lg="2" cols="1" class="align-items-end">
                <b-col>
                    <SearchComponent
                        v-model="selectedDiagnosis" label="Search ICD10" search-u-r-l="/Diagnosis"
                        :disabled="disable" clearable :results-modifier="formatResults" />
                </b-col>
                <b-col v-if="patientDiagnosisOptions.length">
                    <label>Add from patient diagnoses:</label>
                    <Multiselect
                        v-model="selectedPatientDiagnosis" :options="patientDiagnosisOptions"
                        track-by="icD10Code" :custom-label="diagnosisLabel" :disabled="disable"
                        placeholder="Select a patient diagnosis" @input="addFromPatient" />
                </b-col>
            </b-form-row>

            <b-button v-if="!disable" class="mt-2" variant="success" @click="addDiagnosis">Add Diagnosis</b-button>

            <b-table-simple v-show="selectedDiagnoses.length > 0" class="mt-2">
                <b-thead>
                    <b-tr>
                        <b-th>Code</b-th>
                        <b-th>Description</b-th>
                        <b-th class="remove"></b-th>
                    </b-tr>
                </b-thead>
                <b-tbody>
                    <b-tr v-for="diagnosis in selectedDiagnoses" :key="diagnosis.icD10Code">
                        <b-td>{{ diagnosis.icD10Code }}</b-td>
                        <b-td>{{ diagnosis.icD10Description }}</b-td>
                        <b-td>
                            <b-button v-if="!disable" variant="danger" size="sm" @click="removeDiagnosis(diagnosis)">
                                Remove
                            </b-button>
                        </b-td>
                    </b-tr>
                </b-tbody>
            </b-table-simple>
            <small class="text-muted d-block mt-1">
                Diagnosis codes are optional and are saved with the prescription. Any code not already on
                the patient is added to the patient record on save.
            </small>
        </b-col>
    </b-form-row>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import axios from 'axios';
import Multiselect from 'vue-multiselect';
import SearchComponent from '@/components/SearchComponent.vue';
import { ICD10Term, RxDiagnosis, GSDDPatientDiagnosis } from '@/models/GSDD';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { dedupeIcd10Results } from '@/util/icd10Results';

@Component({
    name: 'RxDiagnosisComponent',
    components: {
        SearchComponent,
        Multiselect,
    },
})
export default class RxDiagnosisComponent extends Vue {
    @Prop({ default: () => [] }) private value!: RxDiagnosis[];
    @Prop({ default: false }) private disable!: boolean;
    @Prop({ default: null }) private patientId!: number | null;
    @Prop({ default: null }) private storeId!: number | null;
    @Prop({ default: null }) private rxNumber!: number | null;
    @Prop({ default: null }) private rfNumber!: number | null;

    private selectedDiagnosis: ICD10Term = new ICD10Term();
    private selectedPatientDiagnosis: GSDDPatientDiagnosis | null = null;
    protected patientDiagnosisOptions: GSDDPatientDiagnosis[] = [];

    // Local copy so the list renders reliably regardless of the parent property's Vue 2 reactivity
    // (prescription.diagnosisCodes may start undefined). Every change is emitted back for the save payload.
    protected selectedDiagnoses: RxDiagnosis[] = [];

    @Watch('value', { immediate: true })
    onValueChanged(val: RxDiagnosis[] | undefined) {
        this.selectedDiagnoses = val ? [...val] : [];
    }

    private commit() {
        this.$emit('input', this.selectedDiagnoses);
    }

    created() {
        this.loadRxDiagnoses();
        this.loadPatientDiagnoses();
    }

    @Watch('patientId')
    onPatientChanged() {
        this.loadPatientDiagnoses();
    }

    @Watch('rxNumber')
    onRxChanged() {
        this.loadRxDiagnoses();
    }

    async loadRxDiagnoses() {
        // Only existing fills have persisted Rx diagnosis codes to load. A new Rx keeps whatever
        // codes the user selects locally until the Rx is saved.
        if (!this.storeId || !this.rxNumber || this.rfNumber === null || this.rfNumber === undefined) return;
        try {
            const resp = await axios.get<RxDiagnosis[]>(
                `/Diagnosis/Rx/${this.storeId}/${this.rxNumber}/${this.rfNumber}`);
            this.selectedDiagnoses = resp.data ?? [];
            this.commit();
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    async loadPatientDiagnoses() {
        if (!this.patientId) {
            this.patientDiagnosisOptions = [];
            return;
        }
        try {
            const resp = await axios.get<GSDDPatientDiagnosis[]>(`/Diagnosis/Patient/${this.patientId}`);
            this.patientDiagnosisOptions = resp.data;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    diagnosisLabel(diagnosis: GSDDPatientDiagnosis): string {
        return diagnosis.icD10Description
            ? `${diagnosis.icD10Code} - ${diagnosis.icD10Description}`
            : diagnosis.icD10Code;
    }

    addFromPatient(diagnosis: GSDDPatientDiagnosis | null) {
        if (!diagnosis) return;
        this.addCode(diagnosis.icD10Code, diagnosis.icD10Description);
        this.selectedPatientDiagnosis = null;
    }

    addDiagnosis() {
        if (!this.selectedDiagnosis?.icD10Code?.trim()) {
            this.$notification(NotificationOptions.error('Please select a diagnosis.'));
            return;
        }
        this.addCode(this.selectedDiagnosis.icD10Code, this.selectedDiagnosis.icD10Description);
        this.selectedDiagnosis = new ICD10Term();
    }

    private addCode(icD10Code: string, icD10Description?: string) {
        const code = icD10Code?.trim();
        if (!code) return;
        if (this.selectedDiagnoses.some(d => d.icD10Code?.toUpperCase() === code.toUpperCase())) {
            this.$notification(NotificationOptions.error('This diagnosis has already been added.'));
            return;
        }
        this.selectedDiagnoses = [...this.selectedDiagnoses, { icD10Code: code, icD10Description }];
        this.commit();
    }

    removeDiagnosis(diagnosis: RxDiagnosis) {
        this.selectedDiagnoses = this.selectedDiagnoses.filter(
            d => d.icD10Code?.toUpperCase() !== diagnosis.icD10Code?.toUpperCase());
        this.commit();
    }

    formatResults(items: ICD10Term[]) {
        return dedupeIcd10Results(items);
    }
}
</script>

<style scoped lang="scss">
th.remove {
    width: 110px;
}
</style>
